// Senvè Welcome Offer popup.
//
// The email form is a real Shopify native `customer` form and submits as a
// normal full-page POST (see the header comment in
// snippets/welcome-offer.liquid for why - this store's customer forms
// require solving a visible hCaptcha challenge, and intercepting the
// submit with fetch() skips that entirely and always fails). This script
// only handles: the popup chrome (open/close/focus/backdrop), the
// suppression, and auto-opening already in its success (or
// validation-error) state after a real form submission redirects back
// here.
//
// Trigger: whichever comes first, once - a 20s dwell timer, or desktop
// exit-intent (pointer leaving through the top of the viewport). Only on
// home/product/collection templates (enforced both server-side in
// theme.liquid and here via data-template).
// Suppression (all via localStorage, no cookies): closing it suppresses
// for 7 days (timestamp + TTL, never a permanent flag); a successful
// signup suppresses permanently; it never shows to paid traffic landing
// on a product page (an ad click already has a destination - a modal over
// it just taxes the click we paid for); never shows while
// the cart drawer is open, and opening the cart drawer while the popup is
// open closes it. Coexists with the mobile sticky Add to Cart via a CSS
// rule (body.senve-welcome-offer-open hides it) rather than touching the
// sticky CTA's own logic.
//
// This file also owns the SENVÈ Private Offer recall tab (the block just
// below `previouslyFocused`): the small bottom-left affordance that
// reopens this same popup after it has been closed. It lives here on
// purpose - one popup, one state system, one open()/close() path.
(() => {
  const root = document.getElementById('senve-welcome-offer');
  if (!root) return;

  const WELCOME_KEY = 'senve:welcome-offer';
  const STORAGE_KEY = WELCOME_KEY;
  const SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000;
  const justSubscribed = root.dataset.justSubscribed === 'true';
  const hasErrors = root.dataset.hasErrors === 'true';
  const autoOpen = justSubscribed || hasErrors;

  // Paid traffic landing on a PDP: the ad already delivered the visitor to
  // a page with a buy button, so never interrupt it with the popup. Checked
  // before anything else, but after `autoOpen` so a visitor who actually
  // submitted the form still sees the result if the redirect kept the
  // campaign params.
  const isPaidPdpLanding =
    /\/products\//.test(location.pathname) && /utm_source|utm_medium|fbclid|gclid|ttclid/.test(location.search);
  if (isPaidPdpLanding && !autoOpen) return;

  const readState = (key = STORAGE_KEY) => {
    try {
      return JSON.parse(localStorage.getItem(key) || '{}');
    } catch (e) {
      return {};
    }
  };
  const writeState = (patch, key = STORAGE_KEY) => {
    try {
      localStorage.setItem(key, JSON.stringify({ ...readState(key), ...patch }));
    } catch (e) {
      /* localStorage unavailable (private browsing etc.) - popup just won't persist suppression */
    }
  };

  const state = readState();

  // The old eligibility check ended this script on a still-suppressed
  // dismissal. It now only decides whether the popup may open itself:
  // suppressed-but-inside-TTL means the offer still stands, so the recall
  // tab takes over instead of the script bailing out.
  let armAutoTriggers = true;

  if (justSubscribed) {
    writeState({ subscribed: true }, WELCOME_KEY);
  } else if (!hasErrors) {
    if (readState(WELCOME_KEY).subscribed) return;

    const allowedTemplates = ['index', 'product', 'collection'];
    const currentTemplate = document.body.dataset.senveTemplate || '';
    if (!allowedTemplates.includes(currentTemplate)) return;

    // dismissedAt + TTL: 6 days old -> still suppressed, 8 days -> eligible.
    // `dismissedUntil` is a legacy record written as an absolute expiry.
    const suppressed =
      (state.dismissedAt && Date.now() - state.dismissedAt < SUPPRESS_MS) ||
      (state.dismissedUntil && Date.now() < state.dismissedUntil);
    if (suppressed) armAutoTriggers = false;
  }

  const backdrop = root.querySelector('[data-welcome-dismiss].senve-welcome-offer__backdrop');
  const panel = root.querySelector('.senve-welcome-offer__panel');
  const closeBtn = root.querySelector('.senve-welcome-offer__close');
  const successState = root.querySelector('[data-welcome-success]');
  const codeText = root.querySelector('[data-welcome-code-text]');
  const copyBtn = root.querySelector('[data-welcome-copy]');
  const cartDrawer = document.querySelector('cart-drawer');

  let opened = false;
  let previouslyFocused = null;

  // ---------------------------------------------------------------
  // SENVÈ Private Offer recall tab
  //
  // Deliberately NOT a second state system: it reads the same
  // localStorage record as the popup and adds exactly one key of its
  // own - a sessionStorage flag for "I explicitly dismissed the tab",
  // which is session-scoped by design (it should die with the tab, not
  // sit in localStorage for days). Reopening via the tab goes through
  // the popup's own open() so the focus trap, cart-drawer coexistence
  // and success state all behave identically to an auto-open, and it
  // never rewrites dismissedAt.
  // ---------------------------------------------------------------
  const TAB_DISMISS_KEY = 'senve:private-offer-dismissed';
  const TAB_REVEAL_DELAY = 350;
  const TAB_LEAVE_MS = 180;
  const tab = document.getElementById('senve-offer-tab');
  const tabOpenBtn = tab && tab.querySelector('[data-senve-offer-tab-open]');
  const tabDismissBtn = tab && tab.querySelector('[data-senve-offer-tab-dismiss]');
  const stickyCta = document.querySelector('.senve-sticky-cta');
  let tabRevealId = null;
  let tabLeaveId = null;

  const tabDismissedThisSession = () => {
    try {
      return sessionStorage.getItem(TAB_DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  // successState only exists in the DOM after a real signup, and the
  // snippet doesn't render the tab at all in that case - both checks are
  // cheap insurance that a converted visitor never sees it again.
  const tabAllowed = () =>
    !!tab && !successState && !justSubscribed && !readState(WELCOME_KEY).subscribed && !tabDismissedThisSession();

  // On a PDP the sticky Add to Cart bar owns the bottom of the screen.
  // Read its height at runtime rather than hardcoding one, and only lift
  // while it is actually on screen (senve-gallery.js toggles .is-visible).
  function positionTab() {
    if (!tab) return;
    const lift = stickyCta && stickyCta.classList.contains('is-visible') ? stickyCta.offsetHeight + 12 : 0;
    tab.style.setProperty('--senve-offer-tab-lift', lift + 'px');
  }

  function hideTab(animate) {
    if (!tab) return;
    window.clearTimeout(tabRevealId);
    window.clearTimeout(tabLeaveId);
    tab.classList.remove('is-visible');
    if (!animate) {
      tab.hidden = true;
      tab.classList.remove('is-leaving');
      return;
    }
    tab.classList.add('is-leaving');
    tabLeaveId = window.setTimeout(() => {
      tab.hidden = true;
      tab.classList.remove('is-leaving');
    }, TAB_LEAVE_MS);
  }

  function showTab(delay) {
    if (!tabAllowed() || opened) return;
    window.clearTimeout(tabRevealId);
    tabRevealId = window.setTimeout(() => {
      if (!tabAllowed() || opened) return;
      window.clearTimeout(tabLeaveId);
      positionTab();
      tab.classList.remove('is-leaving');
      tab.hidden = false;
      // Force layout so the reveal actually transitions from the hidden
      // state instead of jumping straight to it.
      // eslint-disable-next-line no-unused-expressions
      tab.offsetHeight;
      tab.classList.add('is-visible');
    }, delay);
  }

  if (tab) {
    tabOpenBtn.addEventListener('click', () => open());
    if (tabDismissBtn) tabDismissBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      try {
        sessionStorage.setItem(TAB_DISMISS_KEY, '1');
      } catch (e) {
        /* sessionStorage unavailable - the tab just won't stay dismissed */
      }
      hideTab(true);
    });
    // Passive, and it only does work while the tab is actually on screen.
    window.addEventListener(
      'scroll',
      () => {
        if (!tab.hidden) positionTab();
      },
      { passive: true }
    );

    // The bar's own visibility is decided by an IntersectionObserver, which
    // resolves after the scroll event that caused it - so a scroll-only
    // recalculation reads the pre-toggle state and leaves the pill sitting
    // on top of the bar until the next scroll. React to the toggle itself.
    if (stickyCta) {
      new MutationObserver(() => {
        if (!tab.hidden) positionTab();
      }).observe(stickyCta, { attributes: true, attributeFilter: ['class'] });
    }
  }

  // The cart drawer's own open sequence adds `overflow-hidden` to <body>
  // synchronously, before the `active` class lands on the drawer a frame
  // later (see cart-drawer.js) - checking both closes that timing gap
  // instead of only reacting after the drawer has finished opening.
  const cartIsOpen = () =>
    (!!cartDrawer && cartDrawer.classList.contains('active')) ||
    document.body.classList.contains('overflow-hidden');

  const getFocusable = () =>
    Array.from(
      panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);

  // Containment via `focusin` (fires on the element that gains focus,
  // bubbles) rather than only intercepting Tab: this also catches focus
  // moving outside the panel programmatically, not just via keyboard.
  const onFocusIn = (event) => {
    if (panel.contains(event.target)) return;
    const focusable = getFocusable();
    (focusable[0] || panel).focus();
  };

  const onKeydown = (event) => {
    if (event.key === 'Escape') close();
  };

  function open() {
    if (opened || (cartIsOpen() && !autoOpen)) return;
    opened = true;
    // The tab is the affordance for a closed popup - it fades out as the
    // popup takes over, whichever path opened it.
    hideTab(true);
    previouslyFocused = document.activeElement;
    // Re-enter the accessibility tree BEFORE focusing anything inside.
    root.inert = false;
    root.removeAttribute('aria-hidden');
    root.hidden = false;
    root.setAttribute('data-state', 'entering');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('senve-welcome-offer-open');
    document.body.style.overflow = 'hidden';
    // Force layout so the entering transform/opacity actually transitions
    // instead of jumping straight to the open state.
    // eslint-disable-next-line no-unused-expressions
    root.offsetHeight;
    root.removeAttribute('data-state');
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusIn);
    // Focus the dialog container itself (tabindex=-1) rather than the
    // first control: standard modal pattern, and it avoids painting a
    // focus ring on the close button for pointer users on open. Tab from
    // here lands on the close button first.
    panel.focus({ preventScroll: true });
  }

  function close() {
    if (!opened) return;
    opened = false;
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('focusin', onFocusIn);
    // Leave immediately: hidden + inert + aria-hidden in the same tick so
    // the dialog is out of the focus/accessibility flow at once (no exit
    // animation - nothing interactive-looking lingers on screen).
    panel.setAttribute('aria-hidden', 'true');
    root.setAttribute('aria-hidden', 'true');
    root.inert = true;
    root.hidden = true;
    root.removeAttribute('data-state');
    document.body.classList.remove('senve-welcome-offer-open');
    document.body.style.overflow = '';
    if (previouslyFocused && document.body.contains(previouslyFocused) && previouslyFocused !== document.body) {
      previouslyFocused.focus();
    } else {
      const main = document.getElementById('MainContent');
      if (main) main.focus();
    }
    if (!successState) {
      writeState({ dismissedAt: Date.now() });
      // Offer closed, not declined: leave the recall tab behind.
      showTab(TAB_REVEAL_DELAY);
    }
  }

  // Every dismiss control (backdrop, X, "Continue Shopping") closes and
  // records the same dismissal timestamp.
  root.querySelectorAll('[data-welcome-dismiss]').forEach((el) => el.addEventListener('click', () => close()));

  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(codeText.textContent.trim());
        copyBtn.textContent = 'Copied';
        copyBtn.dataset.copied = 'true';
        window.setTimeout(() => {
          copyBtn.textContent = 'Copy';
          delete copyBtn.dataset.copied;
        }, 2000);
      } catch (err) {
        /* clipboard API unavailable - the code is already selectable as plain text */
      }
    });
  }

  // Cart drawer coexistence: never open while the cart is open, and close
  // immediately if the visitor opens the cart while the popup is showing.
  if (cartDrawer) {
    new MutationObserver(() => {
      if (cartIsOpen() && opened) close();
    }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(() => {
      if (cartIsOpen() && opened) close();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  if (autoOpen) {
    // Skip the immediate-open check entirely - the visitor just completed
    // a real form submission (successful or not), so show the result
    // right away.
    open();
    return;
  }

  // Popup suppressed by an earlier dismissal that is still inside its TTL:
  // no auto-open, but the offer stands, so the tab carries it across the
  // rest of the visit.
  if (!armAutoTriggers) {
    showTab(TAB_REVEAL_DELAY);
    return;
  }

  // Two independent triggers, first one wins and both then stop listening:
  // a 20s dwell timer, and desktop exit-intent (the pointer leaving through
  // the top edge of the viewport - `relatedTarget` is null only when it
  // actually left the document, not when it crossed onto another element).
  // User rule 2026-09-20: the offer shows the moment the page loads, no
  // dwell and no exit-intent wait. Exit-intent stays wired only as a
  // fallback in case the immediate open is deferred by an open cart drawer.
  const EXIT_TIMER_MS = 0;
  let timerId = null;

  function onMouseOut(event) {
    if (event.clientY > 0 || event.relatedTarget) return;
    trigger();
  }

  function trigger() {
    // Leave the triggers armed while the cart drawer is open so the popup
    // is deferred rather than silently burned.
    if (cartIsOpen()) return;
    window.clearTimeout(timerId);
    document.removeEventListener('mouseout', onMouseOut);
    open();
  }

  function armTriggers() {
    timerId = window.setTimeout(trigger, EXIT_TIMER_MS);
    document.addEventListener('mouseout', onMouseOut);
  }

  if (document.readyState === 'complete') armTriggers();
  else window.addEventListener('load', armTriggers);
})();
